import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { repGet, repPost } from './reports/reportsApi';
import { adminGet } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Card, GREEN, mono } from './bfsi/bfsiShared';
import { SummaryCards, ReportTable, ReportChart, GapsNote } from './reports/ReportWidgets';
import { ExportBar, ExportsPanel, SchedulesPanel } from './reports/ExportsSchedules';
import { PinButton } from './reports/PinButton';

function RoutingTab({ tenantId }) {
  const [tripCode, setTripCode] = useState('');
  const [trips, setTrips] = useState([]);
  const [replay, setReplay] = useState(null);
  useEffect(() => {
    if (!tenantId) return;
    adminGet(`/trips?tenant_id=${tenantId}`).then(t => setTrips(t.trips || t)).catch(() => setTrips([]));
  }, [tenantId]);
  const load = async (tripId) => {
    try { setReplay(await repGet(`/routes/${tripId}`)); } catch (e) { toast.error(e.message); }
  };
  const match = trips.find(t => String(t.id) === tripCode);
  const pts = (replay?.points || []).map(p => [p.lat, p.lon]);
  return (
    <div data-testid="routing-tab">
      <div className="flex gap-2 mb-4 items-end">
        <div className="w-72">
          <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Trip</label>
          <select data-testid="routing-trip-select" value={tripCode} onChange={e => setTripCode(e.target.value)}
            className="w-full h-9 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
            <option value="">— pick a trip —</option>
            {trips.map(t => <option key={t.id} value={t.id}>{t.trip_code || t.trip_number || t.id}</option>)}
          </select>
        </div>
        <Button data-testid="routing-load-btn" disabled={!match} onClick={() => load(match.id)}
          className="text-white h-9" style={{ background: GREEN }}>Replay route</Button>
      </div>
      {replay && (
        <>
          <SummaryCards summary={{ trip: replay.trip_code, device: replay.track0tic_id, km: replay.km, stops: replay.stops.length, bag_opens: replay.bag_opens.length }} />
          <GapsNote locationsHidden={replay.locations_hidden} gaps={[]} />
          {pts.length > 0 && (
            <Card className="p-0 overflow-hidden mb-5" data-testid="routing-map">
              <MapContainer center={pts[Math.floor(pts.length / 2)]} zoom={12} style={{ height: 320 }}>
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                <Polyline positions={pts} pathOptions={{ color: GREEN, weight: 3 }} />
                {replay.stops.map((s, i) => {
                  const idx = replay.points.findIndex(p => p.t === s.arrived_at);
                  const at = idx >= 0 ? [replay.points[idx].lat, replay.points[idx].lon] : null;
                  return at && <CircleMarker key={`${s.arrived_at}-${s.site || i}`} center={at} radius={7} pathOptions={{ color: '#E4572E' }}>
                    <Popup>{s.site || 'Stop'} · {s.halt_minutes} min halt</Popup></CircleMarker>;
                })}
              </MapContainer>
            </Card>
          )}
          <ReportTable columns={[{ key: 'arrived_at', label: 'Arrived' }, { key: 'left_at', label: 'Left' },
            { key: 'halt_minutes', label: 'Halt', unit: 'min' }, { key: 'site', label: 'Site' }]}
            rows={replay.stops} />
          {replay.planned_vs_actual && (
            <Card className="mt-4 text-sm">
              <b>Planned vs actual:</b> planned {replay.planned_vs_actual.planned.join(' → ')} ·
              actual {replay.planned_vs_actual.actual.join(' → ') || '—'} ·
              missed <span style={{ color: '#B3401F' }}>{replay.planned_vs_actual.missed.join(', ') || 'none'}</span>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminReportsPage() {
  const [customers, setCustomers] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState('KILOMETRE');
  const [filters, setFilters] = useState({ from: '', to: '', org_unit_id: '', track0tic_id: '', trip_code: '', passport_short_code: '', group_by: '' });
  const [orgUnits, setOrgUnits] = useState([]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams] = useSearchParams();
  const pinId = searchParams.get('pin');
  const [pendingPin, setPendingPin] = useState(null);

  useEffect(() => {
    const load = async () => {
      const list = await adminGet('/customers-lite');
      setCustomers(list);
      let pin = null;
      if (pinId) {
        pin = (await repGet('/pins').catch(() => [])).find(p => p.id === pinId) || null;
        if (!pin) toast.error('That pinned report no longer exists');
      }
      if (pin) {
        const { tenant_id, ...rest } = pin.filters || {};
        setTenantId(pin.tenant_id || tenant_id || list[0]?.id || '');
        setFilters(f => ({ ...f, ...rest }));
        setPendingPin(pin);
      } else if (list.length) {
        setTenantId(list[0].id);
      }
    };
    load().catch(e => toast.error(e.message));
  }, [pinId]);

  useEffect(() => {
    if (!tenantId) return;
    repGet(`/types?tenant_id=${tenantId}`).then(t => {
      setTypes(t);
      setTab(prev => (t.some(x => x.code === prev) ? prev : (t[0]?.code || 'KILOMETRE')));
    }).catch(e => toast.error(e.message));
    adminGet(`/customers/${tenantId}/org-units`).then(setOrgUnits).catch(() => setOrgUnits([]));
    setResult(null);
  }, [tenantId]);

  const activeFilters = useCallback(() => {
    const f = { tenant_id: tenantId };
    const allowed = types.find(t => t.code === tab)?.filters || [];
    for (const [k, v] of Object.entries(filters)) {
      if (v && allowed.includes(k)) f[k] = v;
    }
    return f;
  }, [tenantId, filters, tab, types]);

  const run = useCallback(async (overrideType, overrideFilters) => {
    setRunning(true);
    try {
      const r = await repPost('/run', { type: overrideType || tab, filters: overrideFilters || activeFilters() });
      if (r.status) {
        toast.info('Large range — running as a background job (see Recent exports)');
        setRefreshKey(k => k + 1);
      } else {
        setResult(r);
      }
    } catch (e) { toast.error(e.message); }
    setRunning(false);
  }, [tab, activeFilters]);

  // Opened from a dashboard pin: once the report types for that customer are in, select the tab and run once.
  useEffect(() => {
    if (!pendingPin || !types.length) return;
    if (!types.some(t => t.code === pendingPin.type)) { setPendingPin(null); return; }
    setTab(pendingPin.type);
    if (pendingPin.type !== 'ROUTING') {
      run(pendingPin.type, { ...pendingPin.filters, tenant_id: pendingPin.tenant_id || pendingPin.filters?.tenant_id });
    }
    setPendingPin(null);
  }, [pendingPin, types, run]);

  const allowed = types.find(t => t.code === tab)?.filters || [];
  const F = (key, label, props = {}) => allowed.includes(key) && (
    <div className="w-40">
      <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>{label}</label>
      <Input data-testid={`filter-${key}`} value={filters[key]} onChange={e => setFilters({ ...filters, [key]: e.target.value })}
        className="h-9 bg-white border-[#D9D6CD]" {...props} />
    </div>
  );

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-reports-page">
      <div className="flex items-end justify-between mb-1">
        <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Reports</h1>
        <div className="w-64">
          <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Customer</label>
          <select data-testid="reports-customer-select" value={tenantId} onChange={e => setTenantId(e.target.value)}
            className="w-full h-9 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <p className="text-sm mb-5" style={{ color: '#9a9e96' }}>
        Every report reads through the visibility rules. Gaps are shown as gaps, never interpolated. Tenant timezone on every export.
      </p>

      <div className="flex gap-1 mb-6 border-b flex-wrap" style={{ borderColor: '#E5E3DC' }}>
        {types.map(t => (
          <button key={t.code} data-testid={`report-tab-${t.code.toLowerCase()}`}
            onClick={() => { setTab(t.code); setResult(null); }}
            className="px-3 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors"
            style={tab === t.code ? { borderColor: GREEN, color: GREEN } : { borderColor: 'transparent', color: '#6b7069' }}>
            {t.name}
          </button>
        ))}
      </div>

      {tab === 'ROUTING' ? <RoutingTab tenantId={tenantId} /> : (
        <>
          <div className="flex flex-wrap gap-3 items-end mb-5">
            {F('from', 'From', { type: 'date' })}
            {F('to', 'To', { type: 'date' })}
            {allowed.includes('org_unit_id') && (
              <div className="w-44">
                <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Org unit</label>
                <select data-testid="filter-org-unit" value={filters.org_unit_id}
                  onChange={e => setFilters({ ...filters, org_unit_id: e.target.value })}
                  className="w-full h-9 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
                  <option value="">All</option>
                  {orgUnits.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}
            {F('track0tic_id', 'Track0tic ID')}
            {F('trip_code', 'Trip code')}
            {F('passport_short_code', 'Passport code')}
            {allowed.includes('group_by') && (
              <div className="w-32">
                <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Group by</label>
                <select data-testid="filter-group-by" value={filters.group_by}
                  onChange={e => setFilters({ ...filters, group_by: e.target.value })}
                  className="w-full h-9 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
                  <option value="">Trip</option><option value="DEVICE">Device</option>
                </select>
              </div>
            )}
            <Button data-testid="run-report-btn" onClick={() => run()} disabled={running}
              className="h-9 text-white" style={{ background: GREEN }}>{running ? 'Running…' : 'Run report'}</Button>
            <PinButton type={tab} filters={activeFilters()} tenantName={customers.find(c => c.id === tenantId)?.name} />
            <div className="ml-auto"><ExportBar type={tab} filters={activeFilters()} onJobCreated={() => setRefreshKey(k => k + 1)} /></div>
          </div>

          {result && (
            <>
              <SummaryCards summary={result.summary} />
              <GapsNote gaps={result.gaps} locationsHidden={result.locations_hidden} />
              <ReportChart series={result.series} />
              <ReportTable columns={result.columns} rows={result.rows} />
              <p className="text-xs mt-2" style={{ color: '#9a9e96' }} data-testid="report-sources">
                Sources: {result.data_sources.join(', ')} · timezone {result.timezone} (PostgreSQL only; no S3 archive layer)
              </p>
            </>
          )}
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <ExportsPanel refreshKey={refreshKey} />
        <SchedulesPanel type={tab} filters={activeFilters()} tenantId={tenantId} />
      </div>
    </div>
  );
}
